"use client";

import { useRef, useState, type ReactNode } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowRight, Paperclip, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RecaptchaComponent from "../recaptcha/RecaptchaComponent";
import {
  BOOK_DEMO_HELP_OPTIONS,
  CONTACT_HELP_OPTIONS,
  FEEDBACK_SOURCES,
  REGION_OPTIONS,
  buildFeedbackFormData,
  type FeedbackSource,
} from "@/utils/feedback-form";
import { isDisposableEmail, getDisposableEmailErrorMessage } from "@/utils/disposable-email-domains";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;

type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  company_url?: string;
  subject?: string;
  message: string;
  consent?: boolean;
};

type FieldErrors = {
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  region: string;
  help_purpose: string;
  message: string;
};

type CmsFeedbackFormProps = {
  source: FeedbackSource;
  submitLabel?: string;
  footerNote?: ReactNode;
  showConsent?: boolean;
};

const inputClassName =
  "w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-[14px] text-[#202020] placeholder:text-[#9CA3AF] focus:border-[#6049E7] focus:outline-none focus:ring-1 focus:ring-[#6049E7]/20";

const labelClassName = "text-[14px] font-semibold text-[#202020]";

const emptyFieldErrors = (): FieldErrors => ({
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  region: "",
  help_purpose: "",
  message: "",
});

const CmsFeedbackForm = ({
  source,
  submitLabel = "Send Message",
  footerNote,
  showConsent = true,
}: CmsFeedbackFormProps) => {
  const isBookDemo = source === FEEDBACK_SOURCES.BOOK_DEMO;
  const helpOptions = isBookDemo ? BOOK_DEMO_HELP_OPTIONS : CONTACT_HELP_OPTIONS;

  const [region, setRegion] = useState<string>("");
  const [helpPurpose, setHelpPurpose] = useState<string>(
    isBookDemo ? "Request a Demo" : "",
  );
  const [files, setFiles] = useState<File[]>([]);
  const [captchaVerified, setCaptchaVerified] = useState<string | null>(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(emptyFieldErrors);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const { register, handleSubmit } = useForm<FormValues>();

  const onFilesSelected = (selected: FileList | null) => {
    if (!selected?.length) return;

    const next: File[] = [...files];
    for (const file of Array.from(selected)) {
      if (next.length >= MAX_FILES) break;
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setErrorMessage(`Each file must be under ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }
      next.push(file);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!captchaVerified) {
      setShowCaptchaError(true);
      return;
    }

    const errors = emptyFieldErrors();
    if (!data.first_name?.trim()) errors.first_name = "Please enter your first name";
    if (!data.last_name?.trim()) errors.last_name = "Please enter your last name";
    if (!data.email?.trim() || !emailRegex.test(data.email)) {
      errors.email = "Please enter a valid email address";
    } else if (isDisposableEmail(data.email)) {
      errors.email = getDisposableEmailErrorMessage(data.email);
    }
    if (!data.company_name?.trim()) {
      errors.company_name = "Please enter your company name";
    }
    if (!region) errors.region = "Please select a region";
    if (!helpPurpose) errors.help_purpose = "Please select how we can help";
    if (!data.message?.trim()) errors.message = "Please enter your message";

    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors(emptyFieldErrors());
    setErrorMessage("");

    const formData = buildFeedbackFormData({
      source,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      company_name: data.company_name,
      company_url: data.company_url,
      region,
      help_purpose: helpPurpose,
      subject: data.subject || helpPurpose,
      message: data.message,
      files,
    });

    try {
      setLoading(true);
      const response = await fetch("/api/cms-feedback-form", {
        method: "POST",
        body: formData,
      });
      const res = await response.json().catch(() => null);

      const isSuccess =
        response.ok &&
        (res?.status === true ||
          res?.success === true ||
          res?.status === "success" ||
          res?.status === "ok");

      if (isSuccess) {
        router.push("/thank-you");
      } else {
        setErrorMessage(
          (typeof res?.message === "string" && res.message) ||
          (typeof res === "string" && res) ||
          "An error occurred while submitting the form.",
        );
      }
    } catch {
      setErrorMessage("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className={labelClassName}>
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="John"
            className={inputClassName}
            {...register("first_name", { required: true })}
          />
          {fieldErrors.first_name && (
            <p className="text-[12px] text-red-500">{fieldErrors.first_name}</p>
          )}
        </div>
        <div className="space-y-2">
          <label className={labelClassName}>
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Doe"
            className={inputClassName}
            {...register("last_name", { required: true })}
          />
          {fieldErrors.last_name && (
            <p className="text-[12px] text-red-500">{fieldErrors.last_name}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className={labelClassName}>
          Work Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          placeholder="john.doe@example.com"
          className={`${inputClassName} ${
            fieldErrors.email
              ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
              : ""
          }`}
          {...register("email", { required: true })}
        />
        {fieldErrors.email && (
          <p className="text-[12px] text-red-500">{fieldErrors.email}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className={labelClassName}>
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Kroolo"
            className={inputClassName}
            {...register("company_name", { required: true })}
          />
          {fieldErrors.company_name && (
            <p className="text-[12px] text-red-500">{fieldErrors.company_name}</p>
          )}
        </div>
        <div className="space-y-2">
          <label className={labelClassName}>Company URL</label>
          <input
            type="url"
            placeholder="https://kroolo.com"
            className={inputClassName}
            {...register("company_url")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className={labelClassName}>
            Region <span className="text-red-500">*</span>
          </label>
          <Select onValueChange={setRegion} value={region || undefined}>
            <SelectTrigger className="h-[48px] w-full rounded-xl border-[#E5E7EB] bg-white text-[14px] text-[#202020] focus:ring-[#6049E7]/20">
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border border-[#E5E7EB] bg-white">
              {REGION_OPTIONS.map((option) => (
                <SelectItem
                  key={option}
                  value={option}
                  className="cursor-pointer rounded-xl py-2 text-[14px] text-[#202020] hover:bg-[#F3F4F6]"
                >
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldErrors.region && (
            <p className="text-[12px] text-red-500">{fieldErrors.region}</p>
          )}
        </div>
        <div className="space-y-2">
          <label className={labelClassName}>
            How can we help? <span className="text-red-500">*</span>
          </label>
          <Select onValueChange={setHelpPurpose} value={helpPurpose || undefined}>
            <SelectTrigger className="h-[48px] w-full rounded-xl border-[#E5E7EB] bg-white text-[14px] text-[#202020] focus:ring-[#6049E7]/20">
              <SelectValue placeholder="Find the best option for you" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border border-[#E5E7EB] bg-white">
              {helpOptions.map((option) => (
                <SelectItem
                  key={option}
                  value={option}
                  className="cursor-pointer rounded-xl py-2 text-[14px] text-[#202020] hover:bg-[#F3F4F6]"
                >
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldErrors.help_purpose && (
            <p className="text-[12px] text-red-500">{fieldErrors.help_purpose}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className={labelClassName}>Subject</label>
        <input
          type="text"
          placeholder={
            isBookDemo
              ? "Enterprise plan pricing"
              : "Brief subject for your request"
          }
          className={inputClassName}
          {...register("subject")}
        />
      </div>

      <div className="space-y-2">
        <label className={labelClassName}>
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          placeholder={
            isBookDemo
              ? "Tell us about your team and what you'd like to see in the demo..."
              : "Tell us how we can help..."
          }
          rows={5}
          className={`${inputClassName} resize-none`}
          {...register("message", { required: true })}
        />
        {fieldErrors.message && (
          <p className="text-[12px] text-red-500">{fieldErrors.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label className={labelClassName}>Attachments (optional)</label>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => onFilesSelected(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-[14px] text-[#6B7280] transition-colors hover:border-[#6049E7] hover:text-[#6049E7]"
        >
          <Paperclip className="h-4 w-4" />
          Add files (max {MAX_FILES}, {MAX_FILE_SIZE_MB}MB each)
        </button>
        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between rounded-lg bg-[#F3F4F6] px-3 py-2 text-[13px] text-[#202020]"
              >
                <span className="truncate pr-2">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="shrink-0 text-[#6B7280] hover:text-red-500"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showConsent && (
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id={`consent-${source}`}
            className="mt-1 h-4 w-4 rounded border-[#E5E7EB] text-[#6049E7] focus:ring-[#6049E7]"
            {...register("consent")}
          />
          <label
            htmlFor={`consent-${source}`}
            className="text-[14px] text-[#6B7280]"
          >
            I agree to the collection, use, and storage of my personal data in
            accordance with Kroolo&apos;s{" "}
            <Link
              href="/legal/privacy-policy"
              className="font-semibold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent underline"
            >
              Privacy Policy
            </Link>
            .
          </label>
        </div>
      )}

      <RecaptchaComponent
        setCaptchaVerified={setCaptchaVerified}
        showCaptchaError={showCaptchaError}
        setShowCaptchaError={setShowCaptchaError}
      />

      {errorMessage && (
        <p className="text-[14px] text-red-500">{errorMessage}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded-[4px] bg-[#6049E7] px-6 py-4 text-[16px] font-semibold text-white transition-colors hover:bg-[#553ED0] disabled:opacity-70"
      >
        {loading ? "Submitting..." : submitLabel}{" "}
        {!loading && <ArrowRight className="h-5 w-5" />}
      </button>

      {footerNote}
    </form>
  );
};

export default CmsFeedbackForm;
